// Report filters in the URL, next to the `report` view parameter of Reports.jsx, which is never touched:
// reload, back / forward and shared links keep the filters. Pure functions; the screens decide when to call them.
//
//   ?report=faults&from=2026-03&to=2026-10&app=jahez,keeta&store=<storeId>&branch=Main&status=Canceled&cause=late&sort=orderAmount&dir=desc&open=<storeId>
//
// Lists are comma separated; a filter change resets `page` (the caller passes the new filters without a page).
// `from` / `to` are the reporting period (absent = the rolling 12 months, utils/reportMonths); older links with `month` open that single month.
// `open` is the expanded store, `sort` / `dir` the order of the rows (default: newest first).

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

export const FILTER_KEYS = Object.freeze({
  month: "month",
  from: "from",
  to: "to",
  apps: "app",
  stores: "store",
  branches: "branch",
  orderStatuses: "status",
  causes: "cause",
  sort: "sort",
  dir: "dir",
  open: "open",
  page: "page",
});

export const SORT_FIELD_PATTERN = /^[A-Za-z]{1,32}$/;
const split = (value) => String(value || "").split(",").map((part) => part.trim()).filter(Boolean);

export function filtersFromSearchParams(searchParams) {
  const get = (name) => (searchParams && typeof searchParams.get === "function" ? searchParams.get(name) : null);
  const month = MONTH.test(get(FILTER_KEYS.month) || "") ? get(FILTER_KEYS.month) : "";
  const from = get(FILTER_KEYS.from);
  const to = get(FILTER_KEYS.to);
  const hasRange = MONTH.test(from || "") && MONTH.test(to || "");
  const page = Number.parseInt(get(FILTER_KEYS.page), 10);
  const sort = get(FILTER_KEYS.sort);
  const dir = String(get(FILTER_KEYS.dir) || "").toLowerCase();
  return {
    from: hasRange ? from : month,
    to: hasRange ? to : month,
    apps: split(get(FILTER_KEYS.apps)),
    stores: split(get(FILTER_KEYS.stores)),
    branches: split(get(FILTER_KEYS.branches)),
    orderStatuses: split(get(FILTER_KEYS.orderStatuses)),
    causes: split(get(FILTER_KEYS.causes)),
    sort: SORT_FIELD_PATTERN.test(sort || "") ? sort : "",
    dir: dir === "asc" || dir === "desc" ? dir : "",
    open: (get(FILTER_KEYS.open) || "").trim(),
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

// Returns a NEW URLSearchParams: every parameter that is not a report filter (report, tab, …) is kept as it was.
export function filtersToSearchParams(filters = {}, current = new URLSearchParams()) {
  const next = new URLSearchParams(current);
  Object.values(FILTER_KEYS).forEach((name) => next.delete(name));
  const list = (key, values) => {
    if (values && values.length) next.set(key, values.join(","));
  };
  if (filters.from && filters.to) {
    next.set(FILTER_KEYS.from, filters.from);
    next.set(FILTER_KEYS.to, filters.to);
  }
  list(FILTER_KEYS.apps, filters.apps);
  list(FILTER_KEYS.stores, filters.stores);
  list(FILTER_KEYS.branches, filters.branches);
  list(FILTER_KEYS.orderStatuses, filters.orderStatuses);
  list(FILTER_KEYS.causes, filters.causes);
  if (filters.sort) next.set(FILTER_KEYS.sort, filters.sort);
  if (filters.dir) next.set(FILTER_KEYS.dir, filters.dir);
  if (filters.open) next.set(FILTER_KEYS.open, filters.open);
  if (filters.page && filters.page > 1) next.set(FILTER_KEYS.page, String(filters.page));
  return next;
}

// Sorting, the open store and the page are not filters: they never narrow what is exported or deleted.
export const hasActiveReportFilters = (filters = {}) =>
  Boolean(filters.from && filters.to) || ["apps", "stores", "branches", "orderStatuses", "causes"].some((name) => filters[name] && filters[name].length > 0);
